import React, { useEffect, useRef, useState } from "react";
import Cat from "@/game/Cat";
import sfx from "@/game/sfx";
import { useTr } from "@/autoTranslate";

/**
 * Yarn Chase — a yarn ball roams around like a mischievous mouse cursor.
 * Move the cat with mouse (or arrow keys) to catch it. 10 catches to win.
 */
const STAGE_W = 640;
const STAGE_H = 360;
const CAT_SIZE = 60;
const YARN_R = 18;

function randPoint(pad = 40) {
  return {
    x: pad + Math.random() * (STAGE_W - pad * 2),
    y: pad + Math.random() * (STAGE_H - pad * 2),
  };
}

export default function MilosYarnChase({ onExit }) {
  const tr = useTr();
  const [best, setBest] = useState(() =>
    parseInt(localStorage.getItem("milos_yarn_best") || "0", 10),
  );
  const [catches, setCatches] = useState(0);
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [cat, setCat] = useState({ x: 60, y: STAGE_H / 2 });
  const [dir, setDir] = useState(1);
  const [yarn, setYarn] = useState({ x: STAGE_W - 80, y: 80, tx: STAGE_W - 80, ty: 80 });
  const rafRef = useRef();
  const lastTsRef = useRef(0);
  const secTsRef = useRef(0);
  const stageRef = useRef(null);
  const mouseRef = useRef(null);
  const keysRef = useRef({});
  const catRef = useRef({ x: 60, y: STAGE_H / 2 });
  const yarnRef = useRef({ x: STAGE_W - 80, y: 80, tx: STAGE_W - 80, ty: 80 });
  const catchesRef = useRef(0);

  const start = () => {
    setCatches(0);
    catchesRef.current = 0;
    setSeconds(0);
    catRef.current = { x: 60, y: STAGE_H / 2 };
    setCat(catRef.current);
    yarnRef.current = { x: STAGE_W - 80, y: 80, tx: STAGE_W - 80, ty: 80 };
    setYarn(yarnRef.current);
    setState("playing");
  };

  useEffect(() => {
    const down = (e) => {
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.code)) {
        keysRef.current[e.code] = true;
        e.preventDefault();
      }
      if (e.code === "Escape") onExit && onExit();
      if ((e.code === "Space" || e.code === "Enter") && state !== "playing") {
        start();
        e.preventDefault();
      }
    };
    const up = (e) => (keysRef.current[e.code] = false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [state, onExit]);

  useEffect(() => {
    const onMove = (e) => {
      if (!stageRef.current) return;
      const r = stageRef.current.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const leave = () => (mouseRef.current = null);
    const el = stageRef.current;
    if (el) {
      el.addEventListener("mousemove", onMove);
      el.addEventListener("mouseleave", leave);
    }
    return () => {
      if (el) {
        el.removeEventListener("mousemove", onMove);
        el.removeEventListener("mouseleave", leave);
      }
    };
  }, [state]);

  useEffect(() => {
    if (state !== "playing") return;
    lastTsRef.current = 0;
    secTsRef.current = 0;
    const loop = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;
      secTsRef.current += dt;
      if (secTsRef.current > 1) {
        secTsRef.current = 0;
        setSeconds((s) => s + 1);
      }

      // Cat follows mouse (if available) or keys
      const target = mouseRef.current;
      let cx = catRef.current.x;
      let cy = catRef.current.y;
      if (target) {
        const desiredX = target.x - CAT_SIZE / 2;
        const desiredY = target.y - CAT_SIZE / 2;
        cx += (desiredX - cx) * 0.22;
        cy += (desiredY - cy) * 0.22;
      }
      const keys = keysRef.current;
      let kdx = 0, kdy = 0;
      if (keys["ArrowLeft"]) kdx -= 1;
      if (keys["ArrowRight"]) kdx += 1;
      if (keys["ArrowUp"]) kdy -= 1;
      if (keys["ArrowDown"]) kdy += 1;
      cx += kdx * 420 * dt;
      cy += kdy * 420 * dt;
      cx = Math.max(0, Math.min(STAGE_W - CAT_SIZE, cx));
      cy = Math.max(0, Math.min(STAGE_H - CAT_SIZE, cy));
      if (kdx !== 0) setDir(Math.sign(kdx));
      else if (target && desiredDir(target.x, catRef.current.x + CAT_SIZE / 2)) {
        setDir(target.x > catRef.current.x + CAT_SIZE / 2 ? 1 : -1);
      }
      catRef.current = { x: cx, y: cy };
      setCat(catRef.current);

      // Yarn behaviour: flee from cat if close, otherwise wander toward target
      const y = yarnRef.current;
      const catCenter = { x: cx + CAT_SIZE / 2, y: cy + CAT_SIZE / 2 };
      const distToCat = Math.hypot(catCenter.x - y.x, catCenter.y - y.y);
      if (distToCat < 110) {
        // Flee
        const dx = y.x - catCenter.x;
        const dy = y.y - catCenter.y;
        const mag = Math.max(1, Math.hypot(dx, dy));
        y.x += (dx / mag) * 460 * dt;
        y.y += (dy / mag) * 460 * dt;
      } else {
        // Wander: move toward target, pick new when reached
        const tdx = y.tx - y.x;
        const tdy = y.ty - y.y;
        const tdist = Math.hypot(tdx, tdy);
        if (tdist < 12) {
          const p = randPoint();
          y.tx = p.x;
          y.ty = p.y;
        } else {
          y.x += (tdx / tdist) * 220 * dt;
          y.y += (tdy / tdist) * 220 * dt;
        }
      }
      y.x = Math.max(YARN_R, Math.min(STAGE_W - YARN_R, y.x));
      y.y = Math.max(YARN_R, Math.min(STAGE_H - YARN_R, y.y));
      yarnRef.current = { ...y };
      setYarn({ ...y });

      // Catch check
      if (distToCat < CAT_SIZE / 2 + YARN_R - 4) {
        catchesRef.current += 1;
        setCatches(catchesRef.current);
        sfx.pop();
        // teleport yarn to random spot
        const p = randPoint(80);
        yarnRef.current = { x: p.x, y: p.y, tx: p.x, ty: p.y };
        setYarn(yarnRef.current);
        if (catchesRef.current >= 10) {
          setState("win");
          sfx.victory();
          if (secTsRef.current || seconds < (best || 999)) {
            const total = seconds + 1;
            if (best === 0 || total < best) {
              setBest(total);
              localStorage.setItem("milos_yarn_best", String(total));
            }
          }
          return;
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [state, best, seconds]);

  return (
    <div className="w-full" data-testid="minigame-milos-yarn" data-minigame-active="true">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Kedi oyunu")}
          </div>
          <h3 className="mt-1 display-serif italic text-4xl md:text-5xl">
            {tr("Yumak Kovalamaca")}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("En iyi süre")} · {best ? `${best}s` : "—"}
          </div>
          <div className="display-serif italic text-3xl">{catches}/10</div>
          <div className="font-mono-label text-[10px] opacity-70">
            {seconds}s
          </div>
        </div>
      </div>

      <div
        ref={stageRef}
        className="mt-6 relative mx-auto rounded-md border-2 border-[color:var(--ink)] overflow-hidden cursor-none"
        style={{
          width: STAGE_W,
          maxWidth: "100%",
          height: STAGE_H,
          background:
            "radial-gradient(circle at 30% 30%, var(--blush), var(--pink)), var(--pink)",
        }}
      >
        {/* Cat */}
        <div
          className="absolute cat-walking"
          style={{
            left: cat.x,
            top: cat.y,
            width: CAT_SIZE,
            height: CAT_SIZE,
          }}
        >
          <Cat dir={dir} sitting={false} size={CAT_SIZE} />
        </div>
        {/* Yarn ball */}
        <div
          className="absolute rounded-full border-2 border-[color:var(--ink)] flex items-center justify-center text-xl"
          style={{
            left: yarn.x - YARN_R,
            top: yarn.y - YARN_R,
            width: YARN_R * 2,
            height: YARN_R * 2,
            background: "var(--pink-hot)",
            transition: "background 0.2s",
          }}
        >
          🧶
        </div>
        {state !== "playing" && (
          <div className="absolute inset-0 flex items-center justify-center flex-col bg-[rgba(255,241,230,0.9)]">
            <div className="display-serif italic text-4xl">
              {state === "idle"
                ? tr("Yumağı kovala")
                : `${tr("Hepsini")} ${seconds}s ${tr("içinde yakaladın!")}`}
            </div>
            <div className="mt-2 font-mono-label text-[10px] uppercase tracking-widest opacity-70">
              {tr("Milkoş'u fareyle (ya da oklarla) hareket ettir · 10 yakalama")}
            </div>
            <div className="mt-4 flex gap-2">
              <button className="btn-pill warm" onClick={start} data-testid="yarn-start">
                {state === "idle" ? tr("Başla") : tr("Tekrar")} →
              </button>
              <button className="btn-pill ghost" onClick={onExit} data-testid="yarn-exit">
                {tr("Çık")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function desiredDir(tx, cx) {
  return Math.abs(tx - cx) > 4;
}
